import React, { useEffect, useState } from 'react';
import AdminRepository from '../../services/AdminRepository.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { AdminFormPageShimmer } from '../common/Shimmer.jsx';
import { devError } from '../../utils/logger.js';
import RegistryLicenseKeyEditor from './RegistryLicenseKeyEditor.jsx';
import { useAdminDialog } from '../../contexts/AdminDialogContext.jsx';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminLicenseSettings.css';
import './LicenseKeyEditor.css';

/**
 * ライセンス設定画面
 */
const AdminLicenseSettings = ({ onUpdate }) => {
  const adminRepo = AdminRepository.getInstance();
  const { showAlert } = useAdminDialog();
  const probe = useScreenProbe('licenses');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [limit, setLimit] = useState(30);
  const [slots, setSlots] = useState([]);

  const loadConfig = async () => {
    probe.start('load');
    setLoading(true);
    try {
      adminRepo.licenseConfigCache = null;
      const config = await adminRepo.getLicenseConfig();
      setLimit(config.limit);
      setSlots(config.names || []);
      probe.end('load', 'ok');
      probe.unlock();
    } catch (err) {
      probe.end('load', `error: ${err?.message || 'unknown'}`);
      devError('ライセンス設定取得エラー:', err);
      await showAlert({ message: 'ライセンス設定の取得に失敗しました。', variant: 'error', title: 'エラー' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleLimitSave = async (e) => {
    e.preventDefault();
    const limitValue = Number(limit);
    if (!Number.isInteger(limitValue) || limitValue < 2) {
      await showAlert({ message: 'ライセンス数は2以上の整数で指定してください。', variant: 'error', title: '入力エラー' });
      return;
    }

    setSaving(true);
    try {
      const config = await adminRepo.getLicenseConfig();
      const namesPayload = {};
      for (const item of config.names || []) {
        if (item?.key) {
          namesPayload[item.key] = item.name === null ? null : (item.name || '');
        }
      }
      await adminRepo.updateLicenseConfig({ limit: limitValue, names: namesPayload });
      await loadConfig();
      if (onUpdate) onUpdate();
      await showAlert({ message: 'ライセンス数を更新しました。', variant: 'success', title: '保存完了' });
    } catch (err) {
      await showAlert({ message: `エラーが発生しました: ${err.message}`, variant: 'error', title: 'エラー' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-list-container admin-license-settings">
      <div className="list-header">
        <div className="section-header">
          <h1>ライセンス管理</h1>
          <p>license-list のスロットとライセンスキーを管理</p>
        </div>
        <button type="button" onClick={loadConfig} className="btn btn-secondary" disabled={loading}>
          更新
        </button>
      </div>

      {loading ? (
        <AdminFormPageShimmer fields={6} withHeader={false} />
      ) : (
        <>
          <form onSubmit={handleLimitSave} className="admin-license-limit-form">
            <div className="form-group">
              <label htmlFor="licenseLimit">ライセンススロット数（上限）</label>
              <div className="admin-license-limit-row">
                <input
                  id="licenseLimit"
                  type="number"
                  min="2"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                />
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? '保存中...' : '上限を保存'}
                </button>
              </div>
              <p className="field-hint">
                スロット数の変更は上限のみ。キーの追加・改名・削除は下の一覧から操作してください。
              </p>
            </div>
          </form>

          <section className="admin-license-keys-section">
            <h2>ライセンスキー一覧</h2>
            <RegistryLicenseKeyEditor slots={slots} onRefresh={loadConfig} />
          </section>
        </>
      )}
    </div>
  );
};

export default AdminLicenseSettings;
